import {
  Snackbar,
  Alert,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  Divider,
} from "@mui/material";

import { useEffect, useState } from "react";
import axios from "axios.js";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";
import DataTable from "theme/Tables/DataTable";

export default function DynamicFlowsSection({
  dataFormRef,
  navigationLogics,
  navigationLogicFields,
  dataFormFields,
  dataFormSections,
  triggerRefresh,
}) {
  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };
  const [tableRows, setTableRows] = useState([]);
  const [selectednavigationLogic, setSelectednavigationLogic] = useState(null);
  const [openNavigationLogicDialog, setOpenNavigationLogicDialog] = useState(false);
  const [selectedNavigationLogicField, setSelectedNavigationLogicField] = useState(null);
  const [selectedNavigationLogicFieldDisplayType, setSelectedNavigationLogicFieldDisplayType] =
    useState(null);
  const [selectedNavigationLogicFieldControlValue, setSelectedNavigationLogicFieldControlValue] =
    useState(null);
  const [selectedNavigationLogicFieldDependant, setSelectedNavigationLogicFieldDependant] =
    useState(null);
  const [openNewNavigationLogicDialog, setOpenNewNavigationLogicDialog] = useState(false);

  useEffect(() => {
    const rowsLogics = buildNavigationLogicRows(navigationLogics);
    setTableRows(rowsLogics);
  }, [navigationLogics]);

  async function createNavigationLogic() {
    if (selectedNavigationLogicField === null) {
      setSeverity("error");
      setErrMsg("A control field is required");
      setOpen(true);
      return;
    }

    if (selectedNavigationLogicFieldControlValue === null) {
      setSeverity("error");
      setErrMsg("Please select control field's value.");
      setOpen(true);
      return;
    }

    if (selectedNavigationLogicFieldDisplayType === null) {
      setSeverity("error");
      setErrMsg("Please select a display type.");
      setOpen(true);
      return;
    }

    if (selectedNavigationLogicFieldDependant === null) {
      setSeverity("error");
      setErrMsg(
        selectedNavigationLogicFieldDisplayType === "SECTION"
          ? "Please select a section."
          : "Please select a form field."
      );
      setOpen(true);
      return;
    }

    setOpenNewNavigationLogicDialog(false);
    setShowLoading(true);

    const json = {
      reference: dataFormRef,
      logic: {
        field: selectedNavigationLogicField.reference,
        type: selectedNavigationLogicFieldDisplayType,
        control: selectedNavigationLogicFieldControlValue,
        dependant: selectedNavigationLogicFieldDependant,
      },
    };

    await axios
      .post("portal/data_forms/navigation_logic/create.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          triggerRefresh();
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function deleteNavigationLogic(ref) {
    setShowLoading(true);
    await axios
      .get(`/portal/data_forms/navigation_logic/${ref}/delete.json`)
      .then((result) => {
        // console.log(result);
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          triggerRefresh();
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  const navigationLogicTableColumns = [
    { Header: "Control Field", accessor: "field", align: "left" },
    { Header: "Section", accessor: "section", align: "left" },
    { Header: "Display Type", accessor: "type", align: "left" },
    // { Header: "Dependants", accessor: "dependants", align: "left" },
    { Header: "Actions", accessor: "actions", align: "right" },
  ];

  const buildNavigationLogicRows = (data) => {
    const rows = [];
    data.map((logic) => {
      rows.push({
        field: logic.field,
        section: logic.section,
        type: logic.type,
        actions: (
          <MDBox display="flex" justifyContent="space-between">
            <MDButton
              size="small"
              variant="outlined"
              color="info"
              iconOnly
              onClick={() => {
                setSelectednavigationLogic(logic);
                setOpenNavigationLogicDialog(true);
              }}
            >
              <Icon sx={{ cursor: "pointer" }} fontSize="small">
                visibility
              </Icon>
            </MDButton>
            &nbsp;
            <MDButton
              size="small"
              variant="outlined"
              color="error"
              iconOnly
              onClick={() => {
                if (window.confirm(`Remove this entry?`) === true) {
                  deleteNavigationLogic(logic.reference);
                }
              }}
            >
              <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                delete
              </Icon>
            </MDButton>
          </MDBox>
        ),
      });
      return null;
    });

    return rows;
  };

  return (
    <>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      {selectednavigationLogic && (
        <Dialog open={openNavigationLogicDialog} fullWidth>
          <DialogTitle>
            <MDBox align="center">
              <Icon>alt_route</Icon>
              <MDTypography variant="h6" fontWeight="regular" align="center">
                <strong>{selectednavigationLogic.type}</strong> Workflow
              </MDTypography>
            </MDBox>
          </DialogTitle>
          <Divider />
          <DialogContent>
            <MDBox>
              <MDBox mb={2}>
                <MDBox mb={2}>
                  <Grid container spacing={3}>
                    <Grid item xs={4} md={4} lg={4} align="center">
                      <MDTypography variant="subtitle2" fontWeight="light" align="left">
                        CONTROL FIELD
                      </MDTypography>
                    </Grid>
                    <Grid item xs={4} md={4} lg={4}>
                      <MDTypography variant="subtitle2" fontWeight="light" align="center">
                        SELECTED VALUE
                      </MDTypography>
                    </Grid>
                    <Grid item xs={4} md={4} lg={4}>
                      <MDTypography variant="subtitle2" fontWeight="light" align="right">
                        DISPLAY
                      </MDTypography>
                    </Grid>
                  </Grid>
                </MDBox>
              </MDBox>
              <MDBox mt={3}>
                <Grid container spacing={5}>
                  <Grid item xs={3} md={3} lg={3}>
                    <MDBox display="flex" alignItems="center" verticalAlign="center">
                      <MDBox>
                        <MDTypography variant="h6" fontWeight="regular">
                          {selectednavigationLogic.field}
                        </MDTypography>
                        <MDTypography variant="h6" fontWeight="light">
                          <small>{selectednavigationLogic.section}</small>
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                  </Grid>
                  <Grid item xs={9} md={9} lg={9}>
                    <MDBox>
                      {selectednavigationLogic.dependants.map((dep) => (
                        <MDBox mb={2}>
                          <Grid container spacing={3}>
                            <Grid item xs={3} md={3} lg={3}>
                              <Icon>east</Icon>
                            </Grid>
                            <Grid item xs={3} md={3} lg={3}>
                              <MDTypography variant="h6" fontWeight="light">
                                {dep.control}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={3} md={3} lg={3}>
                              <Icon>
                                {selectednavigationLogic.type === "SECTION" ? "east" : "hdr_strong"}
                              </Icon>
                            </Grid>
                            <Grid item xs={3} md={3} lg={3}>
                              <MDTypography variant="h6" fontWeight="regular" align="right">
                                {dep.dependant}
                              </MDTypography>
                            </Grid>
                          </Grid>
                        </MDBox>
                      ))}
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton
              onClick={() => {
                setOpenNavigationLogicDialog(false);
              }}
              color="dark"
              variant="outlined"
              iconOnly
              circular
            >
              <Icon>close</Icon>
            </MDButton>
          </DialogActions>
        </Dialog>
      )}

      <Dialog open={openNewNavigationLogicDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDTypography variant="h6" fontWeight="regular">
            New Dynamic Flow
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          <MDBox>
            <MDBox mt={2}>
              <FormControl sx={{ minWidth: 130, width: "100%" }}>
                <InputLabel>Control Field</InputLabel>
                <Select
                  label="Control Field"
                  onChange={(e) => {
                    let f = null;
                    navigationLogicFields.every((fld) => {
                      if (fld.reference === e.target.value) {
                        setSelectedNavigationLogicField(fld);
                        return false;
                      }
                      return true;
                    });
                  }}
                  sx={{ padding: "12px", paddingLeft: "3px" }}
                >
                  {
                    // --> Notice below changes
                    navigationLogicFields.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                      navigationLogicFields.map((record) => (
                        <MenuItem value={`${record.reference}`}>{record.display_name}</MenuItem>
                      ))
                  }
                </Select>
              </FormControl>
            </MDBox>
            {selectedNavigationLogicField && (
              <>
                <MDBox mt={4}>
                  <FormControl sx={{ minWidth: 130, width: "100%" }}>
                    <InputLabel>Control Value</InputLabel>
                    <Select
                      label="Control Value"
                      onChange={(e) => {
                        setSelectedNavigationLogicFieldControlValue(e.target.value);
                      }}
                      sx={{ padding: "12px", paddingLeft: "3px" }}
                    >
                      {
                        // --> Notice below changes
                        selectedNavigationLogicField.options.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                          selectedNavigationLogicField.options.map((record) => (
                            <MenuItem value={`${record.value}`}>{record.name}</MenuItem>
                          ))
                      }
                    </Select>
                  </FormControl>
                </MDBox>
                <MDBox mt={3} align="center">
                  <Icon>south</Icon>
                </MDBox>
                <MDBox mt={3}>
                  <FormControl sx={{ minWidth: 130, width: "100%" }}>
                    <InputLabel>Display Type</InputLabel>
                    <Select
                      label="Display Type"
                      onChange={(e) => {
                        setSelectedNavigationLogicFieldDisplayType(e.target.value);
                      }}
                      sx={{ padding: "12px", paddingLeft: "3px" }}
                    >
                      <MenuItem value="FIELD">Form Field</MenuItem>
                      <MenuItem value="SECTION">Form Section</MenuItem>
                    </Select>
                  </FormControl>
                </MDBox>
                {selectedNavigationLogicFieldControlValue &&
                  selectedNavigationLogicFieldDisplayType && (
                    <MDBox mt={5}>
                      <MDBox mt={3}>
                        {selectedNavigationLogicFieldDisplayType === "FIELD" ? (
                          <FormControl sx={{ minWidth: 120, width: "100%" }}>
                            <InputLabel>Form Field</InputLabel>
                            <Select
                              label="Form Field"
                              onChange={(e) => {
                                setSelectedNavigationLogicFieldDependant(e.target.value);
                              }}
                              sx={{ padding: "12px", paddingLeft: "3px" }}
                            >
                              {
                                // --> Notice below changes
                                dataFormFields.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                                  dataFormFields.map((record) => {
                                    if (
                                      record.reference !== selectedNavigationLogicField.reference
                                    ) {
                                      return (
                                        <MenuItem value={`${record.reference}`}>
                                          {record.display_name}
                                        </MenuItem>
                                      );
                                    }
                                    return null;
                                  })
                              }
                            </Select>
                          </FormControl>
                        ) : (
                          <FormControl sx={{ minWidth: 120, width: "100%" }}>
                            <InputLabel>Form Section</InputLabel>
                            <Select
                              label="Form Section"
                              onChange={(e) => {
                                setSelectedNavigationLogicFieldDependant(e.target.value);
                              }}
                              sx={{ padding: "12px", paddingLeft: "3px" }}
                            >
                              {
                                // --> Notice below changes
                                dataFormSections.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                                  dataFormSections.map((record) => {
                                    if (
                                      record.reference !==
                                      selectedNavigationLogicField.section.reference
                                    ) {
                                      return (
                                        <MenuItem value={`${record.reference}`}>
                                          {record.title}
                                        </MenuItem>
                                      );
                                    }
                                    return null;
                                  })
                              }
                            </Select>
                          </FormControl>
                        )}
                      </MDBox>
                    </MDBox>
                  )}
              </>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenNewNavigationLogicDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="info"
            variant="outlined"
            onClick={() => {
              createNavigationLogic();
            }}
          >
            Save
          </MDButton>
        </DialogActions>
      </Dialog>
      <MDBox>
        <Card sx={{ width: "100%", marginTop: "10px" }}>
          <MDBox p={3} display="flex" justifyContent="space-between">
            <MDBox>
              <MDTypography variant="h6" fontWeight="medium">
                Dynamic Flows
              </MDTypography>
            </MDBox>
            <MDBox>
              <MDButton
                variant="outlined"
                color="dark"
                iconOnly
                size="small"
                onClick={() => {
                  setSelectedNavigationLogicField(null);
                  setSelectedNavigationLogicFieldControlValue(null);
                  setSelectedNavigationLogicFieldDisplayType(null);
                  setSelectedNavigationLogicFieldDependant(null);
                  setOpenNewNavigationLogicDialog(true);
                }}
              >
                <Icon color="dark">add</Icon>
              </MDButton>
            </MDBox>
          </MDBox>
          <MDBox>
            {navigationLogics.length > 0 ? (
              <MDBox mt={1} pb={3}>
                <DataTable
                  table={{ columns: navigationLogicTableColumns, rows: tableRows }}
                  isSorted
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                  canSearch={false}
                />
              </MDBox>
            ) : (
              <MDBox mt={1} align="center">
                <MDBox>
                  <MDTypography variant="h6" fontSize={48}>
                    <Icon>alt_route</Icon>
                  </MDTypography>
                  <MDTypography variant="h6" fontWeight="light" fontSize={16}>
                    Add dynamic workflows to your forms. Control captured data based on user
                    selections.
                  </MDTypography>
                </MDBox>
                <MDBox mt={5} mb={3}>
                  <MDButton
                    variant="contained"
                    color="dark"
                    onClick={() => {
                      setSelectedNavigationLogicField(null);
                      setSelectedNavigationLogicFieldControlValue(null);
                      setSelectedNavigationLogicFieldDisplayType(null);
                      setSelectedNavigationLogicFieldDependant(null);
                      setOpenNewNavigationLogicDialog(true);
                    }}
                    startIcon={<Icon>add</Icon>}
                  >
                    Add Dynamic Flow
                  </MDButton>
                </MDBox>
              </MDBox>
            )}
          </MDBox>
        </Card>
      </MDBox>
    </>
  );
}
